import React, { useState, useEffect, useMemo, useRef } from 'react';
import { SupportedCurrency } from '../../types';
import { useUIStore, AppPage } from '../../stores/useUIStore';
import { useCurrencyStore } from '../../stores/useCurrencyStore';
import { useAuthStore } from '../../stores/useAuthStore';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';

export interface HeaderProps {
  /** Active currency strictly ARS or MXN */
  currency?: SupportedCurrency;
  /** Currency setter */
  onCurrencyChange?: (currency: SupportedCurrency) => void;
  /** Search query (optional for backwards compatibility) */
  searchQuery?: string;
  /** Search query setter (optional for backwards compatibility) */
  onSearchChange?: (query: string) => void;
  /** Current cart item count (optional for backwards compatibility) */
  cartCount?: number;
  /** Cart drawer opener or navigation callback */
  onOpenCart?: () => void;
  /** Optional user profile or login trigger */
  onLogin?: () => void;
  /** Optional deposit modal opener */
  onOpenDeposit?: () => void;
  /** Optional extra classes */
  className?: string;
}

export const Header: React.FC<HeaderProps> = ({
  currency: propCurrency,
  onCurrencyChange,
  className = '',
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activePage = useUIStore((s) => s.activePage);
  const navigateTo = useUIStore((s) => s.navigateTo);
  const openDeposit = useUIStore((s) => s.openDepositModal);
  const openAuth = useUIStore((s) => s.openAuthModal);

  const globalCurrency = useCurrencyStore((s) => s.currency);
  const setGlobalCurrency = useCurrencyStore((s) => s.setCurrency);
  const currency = propCurrency || globalCurrency;

  // Authenticated user state from centralized reactive store
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  // Handle drawer close with animation and timeout cancellation
  const closeMenu = (immediate = false) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (!isMenuOpen && !isMenuClosing) return;
    if (immediate) {
      setIsMenuOpen(false);
      setIsMenuClosing(false);
      unlockScroll('header-menu');
      return;
    }
    setIsMenuClosing(true);
    closeTimeoutRef.current = setTimeout(() => {
      setIsMenuOpen(false);
      setIsMenuClosing(false);
      unlockScroll('header-menu');
      closeTimeoutRef.current = null;
    }, 200);
  };

  // Safe open menu ensuring no lingering closing animations or timers
  const openMenu = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsMenuClosing(false);
    setIsMenuOpen(true);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      unlockScroll('header-menu');
    };
  }, []);

  // Close drawer immediately whenever activePage changes
  useEffect(() => {
    if (isMenuOpen || isMenuClosing) {
      closeMenu(true);
    }
  }, [activePage]);

  // Close drawer on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) {
        closeMenu();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);

  // Prevent background scroll when drawer is open or closing
  const isDrawerActive = isMenuOpen || isMenuClosing;
  useEffect(() => {
    if (isDrawerActive) {
      lockScroll('header-menu');
      return () => {
        unlockScroll('header-menu');
      };
    }
  }, [isDrawerActive]);

  // Trigger deposit modal
  const handleDepositClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isMenuOpen || isMenuClosing) {
      closeMenu(true);
    }
    openDeposit();
  };

  // Header Brand Logo click handler:
  // - If already on landing: smoothly scroll to top and reset landing scroll position
  // - If on another page: navigate to landing and restore previously preserved scroll position
  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isMenuOpen || isMenuClosing) {
      closeMenu(true);
    }
    if (activePage === 'landing') {
      useUIStore.getState().saveScrollPosition('landing', 0);
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      return;
    }
    navigateTo('landing', '/');
  };

  // General Navigation handler for menu items and links:
  // Preserves existing position when clicking "Inicio" from menu while on landing
  const handleNav = (page: AppPage, path: string, e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    if (isMenuOpen || isMenuClosing) {
      closeMenu(true);
    }
    if (activePage === page && page === 'landing') {
      // Continuing where user was on landing without resetting to top
      return;
    }
    navigateTo(page, path);
  };

  // Auth trigger (login / register)
  const handleAuthClick = (mode: 'login' | 'register') => {
    if (isMenuOpen || isMenuClosing) closeMenu(true);
    openAuth(mode);
  };

  // Logout handler
  const handleLogout = () => {
    logout();
    if (isMenuOpen || isMenuClosing) closeMenu(true);
  };

  const displayName = user
    ? user.fortnite_username ||
      [user.first_name, user.last_name].filter(Boolean).join(' ') ||
      (user.email ? user.email.split('@')[0] : 'Mi Cuenta')
    : 'Mi Cuenta';

  // User balance formatted strictly as number
  const formattedBalance = useMemo(() => {
    const vbucks = user?.balance_vbucks || 0;
    return vbucks.toLocaleString('es-AR');
  }, [user]);

  // Live coupons count
  const [couponsCount, setCouponsCount] = useState<number>(2);

  useEffect(() => {
    if (!user) {
      setCouponsCount(0);
      return;
    }
    const token =
      localStorage.getItem('fn_token') ||
      localStorage.getItem('token') ||
      localStorage.getItem('auth_token');
    if (!token) return;

    fetch('/api/coupons/my-coupons', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (Array.isArray(data)) {
          const active = data.filter(
            (c: any) => !c.is_used && (!c.expires_at || new Date(c.expires_at) > new Date())
          ).length;
          setCouponsCount(active > 0 ? active : 2);
        }
      })
      .catch(() => {});
  }, [user]);

  // Helper to render navigation items with active bar & arrow
  const renderPageItem = (
    page: AppPage,
    path: string,
    label: string,
    iconClass: string,
    badge?: React.ReactNode
  ) => {
    const isActive = activePage === page;
    return (
      <div className="relative">
        <button
          type="button"
          onClick={(e) => handleNav(page, path, e)}
          className={`w-full flex items-center justify-between px-3 py-1.5 transition-all text-left cursor-pointer ${
            isActive
              ? 'bg-[#eff4fe] text-[#1447e6] font-bold border border-[#1447e6]/30 shadow-2xs'
              : 'text-[#06173f] hover:text-[#1447e6] hover:bg-slate-100/70 font-semibold border border-transparent'
          }`}
          style={{ clipPath: 'var(--slash-sm)' }}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <i
              className={`${iconClass} text-[#1447e6] text-sm w-4 text-center shrink-0`}
            />
            <span className="font-chakra text-sm">{label}</span>
            {badge}
          </div>
          {isActive && (
            <div className="w-5 flex items-center justify-center shrink-0 mr-1" aria-hidden="true">
              <i className="fa-solid fa-chevron-right text-xs text-[#1447e6]" />
            </div>
          )}
        </button>
      </div>
    );
  };

  return (
    <>
      {/* 1. STICKY HEADER OFICIAL - Royal Deep Blue (#0a2a9c) 1:1 con diseño original */}
      <header
        className={`sticky top-0 z-50 w-full bg-[#0a2a9c] shadow-md transition-colors select-none ${className}`}
      >
        <div className="max-w-7xl mx-auto px-2.5 sm:px-4 md:px-6 h-14 flex items-center justify-between">
          {/* Left: Brand Logo linking to home / */}
          <a
            href="/"
            onClick={handleLogoClick}
            className="inline-flex items-center gap-2 text-white no-underline group cursor-pointer shrink-0"
            title="elGordoFortnite • Inicio"
            aria-label="elGordoFortnite, ir al inicio"
          >
            <div className="relative w-10 h-10 flex items-center justify-center shrink-0">
              <img
                src="/assets/logo-gf-badge.png"
                alt="GF"
                width="40"
                height="40"
                className="w-10 h-10 object-contain transition-transform duration-200 group-hover:scale-105"
                loading="eager"
                decoding="async"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  const fallback = target.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div
                className="hidden w-10 h-10 bg-white text-[#1447e6] font-chakra font-black italic items-center justify-center text-sm border-2 border-[#cfd7e3]"
                style={{ clipPath: 'var(--g-cut)' }}
              >
                GF
              </div>
            </div>
            <span className="font-chakra font-black italic tracking-tight text-white text-base sm:text-xl md:text-2xl leading-none hidden md:inline">
              elGordoFortnite
            </span>
          </a>

          {/* Right: Actions Container (Saldo, Carrito, Iniciar Sesión, Menú) */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Si está autenticado: Saldo disponible */}
            {user && (
              <button
                type="button"
                onClick={handleDepositClick}
                id="nav-saldo-widget"
                className="h-[38px] px-2.5 sm:px-3 bg-white hover:bg-[#f0f4f9] text-[#06173f] border-2 border-[#cfd7e3] font-chakra font-bold text-xs flex items-center gap-1.5 transition-all cursor-pointer shadow-sm active:scale-95"
                style={{
                  clipPath: 'var(--g-slash)',
                  filter: 'drop-shadow(2px 2px 0 #cfd7e3)',
                }}
                title="Saldo disponible — clic para cargar"
                aria-label="Saldo disponible"
              >
                <span
                  className="w-5 h-5 bg-[#1447e6] text-white flex items-center justify-center font-chakra font-black italic text-[11px] leading-none shrink-0"
                  style={{ clipPath: 'polygon(4px 0, 100% 0, 100% calc(100% - 4px), calc(100% - 4px) 100%, 0 100%, 0 4px)' }}
                >
                  G
                </span>
                <span className="font-chakra font-black tracking-tight text-xs sm:text-sm text-[#06173f]">
                  {(user.balance_vbucks || 0).toLocaleString('es-AR')}
                </span>
                <span className="font-chakra font-bold text-xs text-[#06173f] leading-none select-none">+</span>
              </button>
            )}

            {/* Si no está autenticado: Botón Iniciar Sesión */}
            {!user ? (
              <button
                type="button"
                onClick={() => handleAuthClick('login')}
                id="nav-login-btn"
                className="h-[38px] px-2.5 sm:px-3.5 bg-white hover:bg-[#f0f4f9] text-[#06173f] border-2 border-[#cfd7e3] font-chakra font-bold text-xs flex items-center gap-1.5 transition-all cursor-pointer shadow-sm active:scale-95 shrink-0"
                style={{
                  clipPath: 'var(--g-slash)',
                  filter: 'drop-shadow(2px 2px 0 #cfd7e3)',
                }}
                title="Iniciar sesión"
                aria-label="Iniciar sesión"
              >
                <i className="fa-solid fa-user text-xs text-[#1447e6]" />
                <span className="btn-lbl hidden sm:inline">Iniciar sesión</span>
              </button>
            ) : (
              /* Botón de Carrito para usuario autenticado (oculto en la página de carrito) */
              activePage !== 'cart' && (
                <a
                  href="/cart.html"
                  onClick={(e) => handleNav('cart', '/cart.html', e)}
                  id="nav-cart-btn"
                  className="w-[38px] h-[38px] bg-white hover:bg-[#f0f4f9] text-[#1447e6] border-2 border-[#cfd7e3] flex items-center justify-center transition-all cursor-pointer shadow-sm active:scale-95 no-underline shrink-0"
                  style={{
                    clipPath: 'var(--g-slash)',
                    filter: 'drop-shadow(2px 2px 0 #cfd7e3)',
                  }}
                  title="Ver carrito"
                  aria-label="Carrito de compras"
                >
                  <i className="fa-solid fa-cart-shopping text-sm text-[#1447e6]" />
                </a>
              )
            )}

            {/* Botón de Menú Hamburguesa */}
            <button
              type="button"
              id="nav-menu-toggle-btn"
              onClick={openMenu}
              className="w-[38px] h-[38px] bg-white hover:bg-[#f0f4f9] text-[#1447e6] border-2 border-[#cfd7e3] flex items-center justify-center transition-all cursor-pointer shadow-sm active:scale-95 shrink-0"
              style={{
                clipPath: 'var(--g-slash)',
                filter: 'drop-shadow(2px 2px 0 #cfd7e3)',
              }}
              aria-expanded={isMenuOpen}
              aria-controls="hd-dropdown-panel"
              aria-label="Abrir menú"
              title="Menú"
            >
              <i className="fa-solid fa-bars text-sm text-[#1447e6]" />
            </button>
          </div>
        </div>
      </header>

      {/* 2. SLIDE-OVER NAVIGATION DRAWER & BACKDROP */}
      {(isMenuOpen || isMenuClosing) && (
        <div className="fixed inset-0 z-[200000] flex justify-end">
          {/* Backdrop con desenfoque suave idéntico al carrito */}
          <div
            className={`fixed inset-0 soft-backdrop ${
              isMenuClosing ? 'anim-backdrop-out' : 'anim-backdrop-in'
            }`}
            onClick={() => closeMenu(false)}
            aria-hidden="true"
          />

          {/* Slide-out Menu Panel (Estilo 1:1 con la referencia respetando la identidad visual) */}
          <div
            id="hd-dropdown-panel"
            className={`relative w-[340px] sm:w-[360px] max-w-[88vw] h-full bg-white text-[#06173f] z-10 shadow-2xl flex flex-col border-l border-[#cfd7e3] ${
              isMenuClosing ? 'anim-drawer-out' : 'anim-drawer-in'
            }`}
            role="dialog"
            aria-label="Menú de navegación"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div className="hd-drawer-header px-4 py-3 border-b border-[#cfd7e3] flex items-center justify-between shrink-0 bg-[#f8fafc]">
              <span className="font-chakra font-black tracking-wider text-sm sm:text-base text-[#06173f] uppercase">
                Menú
              </span>
              <button
                type="button"
                onClick={() => closeMenu(false)}
                className="hd-drawer-close w-8 h-8 bg-white hover:bg-[#fee2e2] text-[#475569] hover:text-[#dc2626] border-2 border-[#cfd7e3] hover:border-[#fca5a5] flex items-center justify-center transition-all shadow-xs cursor-pointer active:scale-95"
                style={{ clipPath: 'var(--slash-sm)' }}
                aria-label="Cerrar menú"
                title="Cerrar"
              >
                <i className="fa-solid fa-xmark text-sm" />
              </button>
            </div>

            {/* Drawer Body */}
            <div className="flex-1 flex flex-col overflow-y-auto px-4 py-4 space-y-4 bg-white text-[#06173f] no-scrollbar">
              {/* 1. Account Profile Card */}
              {user ? (
                <div
                  className="hd-menu-user-box bg-[#f8fafc] border-2 border-[#cfd7e3] p-3 pr-3.5 flex items-center justify-between gap-3 shadow-xs"
                  style={{
                    clipPath: 'var(--g-cut)',
                    filter: 'drop-shadow(1px 1px 0 #cfd7e3)',
                  }}
                >
                  <div
                    onClick={(e) => handleNav('profile', '/profile.html', e)}
                    className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer group"
                    title="Ir a mi perfil"
                  >
                    <div
                      className="w-10 h-10 bg-[#1447e6] text-white flex items-center justify-center text-sm font-bold shrink-0 shadow-xs"
                      style={{ clipPath: 'var(--g-cut)' }}
                    >
                      <i className="fa-solid fa-user" />
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <span
                        className="font-chakra font-bold text-sm text-[#06173f] truncate block group-hover:text-[#1447e6] transition-colors leading-tight"
                        title={displayName}
                      >
                        {displayName}
                      </span>
                      <span className="text-xs text-slate-500 block group-hover:text-[#1447e6] transition-colors font-sans mt-0.5">
                        Ver mi perfil
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => handleNav('profile', '/profile.html', e)}
                    className="w-8 h-8 bg-white hover:bg-[#f0f4f9] text-[#475569] hover:text-[#1447e6] border-2 border-[#cfd7e3] hover:border-[#1447e6] flex items-center justify-center text-sm transition-all shadow-xs shrink-0 cursor-pointer active:scale-95"
                    style={{ clipPath: 'var(--slash-sm)' }}
                    title="Configuración de perfil"
                    aria-label="Configuración de perfil"
                  >
                    <i className="fa-solid fa-gear" />
                  </button>
                </div>
              ) : (
                <div
                  className="bg-[#f8fafc] border border-[#d8e1ea] p-2.5 space-y-2.5 shadow-2xs text-left"
                  style={{ clipPath: 'var(--g-cut)' }}
                >
                  <div className="flex items-center gap-2.5">
                    <div
                      className="w-8 h-8 bg-[#1447e6] text-white flex items-center justify-center text-xs font-bold shrink-0"
                      style={{ clipPath: 'var(--g-cut)' }}
                    >
                      <i className="fa-solid fa-user text-xs" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="font-chakra font-bold text-xs sm:text-sm text-[#06173f] block leading-tight">
                        Mi Cuenta
                      </span>
                      <span className="text-[11px] text-slate-500 block">
                        Inicia sesión o regístrate
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 pt-0.5">
                    <button
                      type="button"
                      onClick={() => handleAuthClick('login')}
                      className="h-8 px-2.5 bg-white hover:bg-[#f0f4f9] text-[#1447e6] border border-[#cfd7e3] font-chakra font-bold text-xs flex items-center justify-center gap-1.5 transition-all shadow-2xs cursor-pointer active:scale-95"
                      style={{ clipPath: 'var(--slash-sm)' }}
                    >
                      <i className="fa-solid fa-user text-[11px]" />
                      <span>Entrar</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAuthClick('register')}
                      className="h-8 px-2.5 bg-[#1447e6] hover:bg-[#0a2a9c] text-white border border-[#1447e6] font-chakra font-bold text-xs flex items-center justify-center gap-1.5 transition-all shadow-2xs cursor-pointer active:scale-95"
                      style={{ clipPath: 'var(--slash-sm)' }}
                    >
                      <i className="fa-solid fa-user-plus text-[11px]" />
                      <span>Registro</span>
                    </button>
                  </div>
                </div>
              )}

              {/* 2. Sección CUENTA */}
              <div className="text-left">
                <div className="text-[11px] font-chakra font-bold uppercase tracking-wider text-slate-400 px-3 pb-2">
                  CUENTA
                </div>
                <div className="flex flex-col gap-1">
                  {/* Mi Saldo */}
                  <div className="flex items-center justify-between px-3 py-1.5 rounded-xl hover:bg-slate-100/70 transition-colors">
                    <button
                      type="button"
                      onClick={(e) => {
                        if (!user) {
                          handleAuthClick('login');
                          return;
                        }
                        handleNav('profile', '/profile.html#billetera', e);
                        setTimeout(() => {
                          const el = document.getElementById('billetera');
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 150);
                      }}
                      className="flex items-center gap-2.5 text-left min-w-0 flex-1 cursor-pointer"
                    >
                      <i className="fa-solid fa-wallet text-[#1447e6] text-sm w-4 text-center shrink-0" />
                      <span className="font-chakra font-bold text-sm text-[#06173f]">Mi Saldo</span>
                    </button>

                    {user ? (
                      <button
                        type="button"
                        onClick={handleDepositClick}
                        className="h-7 pl-2.5 pr-1 bg-white hover:bg-[#f0f4f9] text-[#06173f] border border-[#cfd7e3] hover:border-[#1447e6]/50 flex items-center gap-1.5 transition-all cursor-pointer shadow-2xs active:scale-95 shrink-0"
                        style={{ clipPath: 'var(--slash-sm)' }}
                        title="Saldo disponible — clic para cargar"
                      >
                        <span className="font-chakra font-black italic text-xs text-[#1447e6] leading-none select-none shrink-0">
                          G
                        </span>
                        <span className="font-chakra font-black tracking-tight text-xs text-[#06173f]">
                          {formattedBalance}
                        </span>
                        <span
                          className="w-5 h-5 bg-[#1447e6] hover:bg-[#0a2a9c] text-white flex items-center justify-center font-chakra font-black text-xs leading-none ml-1 shadow-2xs select-none transition-colors"
                          style={{ clipPath: 'var(--slash-sm)' }}
                          title="Cargar saldo"
                          aria-label="Cargar saldo"
                        >
                          +
                        </span>
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleAuthClick('login')}
                        className="text-xs font-chakra font-bold text-[#1447e6] hover:underline cursor-pointer mr-1"
                      >
                        Ingresar
                      </button>
                    )}
                  </div>

                  {/* Mis Cuentas */}
                  <button
                    type="button"
                    onClick={(e) => {
                      if (!user) {
                        handleAuthClick('login');
                        return;
                      }
                      handleNav('profile', '/profile.html#cuentas', e);
                      setTimeout(() => {
                        const el = document.getElementById('vincular-cuentas') || document.getElementById('cuentas');
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          el.classList.add('ring-2', 'ring-[var(--royal)]', 'transition-all', 'duration-500');
                          setTimeout(() => el.classList.remove('ring-2', 'ring-[var(--royal)]'), 2500);
                        }
                      }, 150);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-xl hover:bg-slate-100/70 text-[#06173f] hover:text-[#1447e6] transition-colors text-left cursor-pointer font-semibold"
                  >
                    <i className="fa-solid fa-gamepad text-[#1447e6] text-sm w-4 text-center shrink-0" />
                    <span className="font-chakra font-bold text-sm">Mis Cuentas</span>
                  </button>

                  {/* Mis Cupones */}
                  <button
                    type="button"
                    onClick={(e) => {
                      if (!user) {
                        handleAuthClick('login');
                        return;
                      }
                      handleNav('profile', '/profile.html#cupones', e);
                      setTimeout(() => {
                        const el = document.getElementById('cupones');
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          el.classList.add('ring-2', 'ring-[var(--royal)]', 'transition-all', 'duration-500');
                          setTimeout(() => el.classList.remove('ring-2', 'ring-[var(--royal)]'), 2500);
                        }
                      }, 150);
                    }}
                    className="w-full flex items-center justify-between px-3 py-1.5 rounded-xl hover:bg-slate-100/70 text-[#06173f] hover:text-[#1447e6] transition-colors text-left cursor-pointer font-semibold"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <i className="fa-solid fa-ticket text-[#1447e6] text-sm w-4 text-center shrink-0" />
                      <span className="font-chakra font-bold text-sm">Mis Cupones</span>
                    </div>
                    {couponsCount > 0 && (
                      <span
                        className="w-5 h-5 bg-[#1447e6] text-white font-chakra font-black text-xs flex items-center justify-center shadow-2xs shrink-0 leading-none select-none mr-1"
                        style={{ clipPath: 'var(--slash-sm)' }}
                      >
                        {couponsCount}
                      </span>
                    )}
                  </button>

                  {/* Mis Pedidos */}
                  <button
                    type="button"
                    onClick={(e) => {
                      if (!user) {
                        handleAuthClick('login');
                        return;
                      }
                      handleNav('profile', '/profile.html#pedidos', e);
                      setTimeout(() => {
                        const el = document.getElementById('pedidos') || document.getElementById('orders-section');
                        if (el) {
                          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          el.classList.add('ring-2', 'ring-[var(--royal)]', 'transition-all', 'duration-500');
                          setTimeout(() => el.classList.remove('ring-2', 'ring-[var(--royal)]'), 2500);
                        }
                      }, 150);
                    }}
                    className="w-full flex items-center gap-2.5 px-3 py-1.5 rounded-xl hover:bg-slate-100/70 text-[#06173f] hover:text-[#1447e6] transition-colors text-left cursor-pointer font-semibold"
                  >
                    <i className="fa-solid fa-box-archive text-[#1447e6] text-sm w-4 text-center shrink-0" />
                    <span className="font-chakra font-bold text-sm">Mis Pedidos</span>
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-[#cfd7e3] my-2.5" />

              {/* 3. Sección PÁGINAS */}
              <div className="text-left">
                <div className="text-[11px] font-chakra font-bold uppercase tracking-wider text-slate-400 px-3 pb-2">
                  PÁGINAS
                </div>
                <nav className="flex flex-col gap-1" aria-label="Páginas de la web">
                  {renderPageItem('landing', '/', 'Inicio', 'fa-solid fa-house')}
                  {renderPageItem('shop', '/tienda', 'Tienda', 'fa-solid fa-store')}
                  {renderPageItem('recargas', '/recargas', 'Recargas', 'fa-solid fa-coins')}
                  {renderPageItem(
                    'sorteos',
                    '/sorteos',
                    'Sorteos',
                    'fa-solid fa-gift',
                    <span
                      className="bg-[#dcfce7] text-[#15803d] border border-green-300 font-chakra font-black text-[9px] px-1.5 py-0.5 uppercase tracking-wide leading-none ml-1 shadow-2xs"
                      style={{ clipPath: 'var(--slash-sm)' }}
                    >
                      ¡NUEVO!
                    </span>
                  )}
                </nav>
              </div>

              {/* 4. Precios en (Selector de Moneda) - Bajada con mt-auto y separación */}
              <div className="mt-auto pt-4 pb-1 space-y-3">
                <div className="border-t border-[#cfd7e3]" />
                <div className="flex items-center justify-between px-3">
                  <span className="font-chakra font-bold text-xs text-[#06173f]">Precios en</span>
                  <div
                    className="cu-light inline-flex items-center bg-[#edf2f7] p-0.5 border border-[#cfd7e3] shadow-2xs"
                    style={{ clipPath: 'var(--slash-sm)' }}
                    role="group"
                    aria-label="Seleccionar moneda"
                  >
                    {(['ARS', 'MXN'] as SupportedCurrency[]).map((c) => {
                      const isActive = currency === c;
                      return (
                        <button
                          key={c}
                          type="button"
                          className={`font-chakra font-bold text-xs py-0.5 px-2.5 transition-all cursor-pointer border-0 ${
                            isActive
                              ? 'on bg-[#1447e6] !bg-[#1447e6] text-white !text-white shadow-2xs'
                              : 'bg-transparent text-[#475569] hover:text-[#06173f]'
                          }`}
                          style={{
                            clipPath: 'var(--slash-sm)',
                            ...(isActive
                              ? { backgroundColor: '#1447e6', color: '#ffffff' }
                              : { backgroundColor: 'transparent', color: '#475569' }),
                          }}
                          aria-pressed={isActive}
                          onClick={() => {
                            setGlobalCurrency(c);
                            if (onCurrencyChange) onCurrencyChange(c);
                          }}
                        >
                          {c}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* 5. Drawer Footer Actions (Soporte WhatsApp / Instagram / Cerrar sesión) */}
            <div className="px-3.5 py-2.5 border-t border-[#cfd7e3] bg-white space-y-2 shrink-0 text-left">
              <div className="grid grid-cols-2 gap-2">
                {/* Botón Soporte WhatsApp */}
                <a
                  href="https://wa.me/5492353421028?text=Hola,%20necesito%20ayuda%20con%20elGordoFortnite"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => closeMenu(false)}
                  className="h-9 px-2.5 bg-white hover:bg-[#f0fdf4] text-[#06173f] hover:text-emerald-700 border border-[#cfd7e3] hover:border-emerald-500 font-chakra font-bold text-xs flex items-center justify-center gap-1.5 transition-all shadow-2xs no-underline group cursor-pointer active:scale-95"
                  style={{ clipPath: 'var(--slash-sm)' }}
                  title="Soporte oficial por WhatsApp"
                >
                  <i className="fa-brands fa-whatsapp text-sm text-emerald-500 group-hover:scale-110 transition-transform" />
                  <span>Soporte</span>
                </a>

                {/* Botón Instagram */}
                <a
                  href="https://www.instagram.com/elgordofortnite/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => closeMenu(false)}
                  className="h-9 px-2.5 bg-white hover:bg-[#fdf2f8] text-[#06173f] hover:text-[#e1306c] border border-[#cfd7e3] hover:border-[#e1306c] font-chakra font-bold text-xs flex items-center justify-center gap-1.5 transition-all shadow-2xs no-underline group cursor-pointer active:scale-95"
                  style={{ clipPath: 'var(--slash-sm)' }}
                  title="Instagram oficial @elgordofortnite"
                >
                  <i className="fa-brands fa-instagram text-sm text-[#e1306c] group-hover:scale-110 transition-transform" />
                  <span>Instagram</span>
                </a>
              </div>

              {/* Cerrar sesión / Iniciar sesión */}
              {user ? (
                <button
                  type="button"
                  onClick={handleLogout}
                  className="hd-menu-user-logout w-full h-9 px-3 bg-[#f8fafc] hover:bg-rose-50 text-[#b91c1c] border border-rose-200/80 hover:border-rose-400 font-chakra font-bold text-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-2xs active:scale-95"
                  style={{ clipPath: 'var(--slash-sm)' }}
                >
                  <i className="fa-solid fa-arrow-right-from-bracket text-xs text-[#b91c1c]" />
                  <span>Cerrar sesión</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => handleAuthClick('login')}
                  className="w-full h-9 px-3 bg-[#1447e6] hover:bg-[#0a2a9c] text-white border border-[#1447e6] font-chakra font-bold text-xs flex items-center justify-center gap-1.5 transition-all cursor-pointer shadow-2xs active:scale-95"
                  style={{ clipPath: 'var(--slash-sm)' }}
                >
                  <i className="fa-solid fa-user text-xs" />
                  <span>Iniciar sesión</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
